import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MultiSelectFieldSettings, RadioButton } from 'crawford-shell';
import { uniqBy } from 'lodash';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { CodeNamePair } from '../../model/code-name-pair.model';
import { SearchFilterService } from '../services/search-filter.service';
import { AccordionService } from '../services/accordion.service';
import { Observable } from 'rxjs/Observable';

@Component({
  selector: 'app-claim-status-search',
  templateUrl: './claim-status-search.component.html',
  styleUrls: ['./claim-status-search.component.scss']
})
export class ClaimStatusSearchComponent
  implements OnInit {

  @Input() public group: FormGroup;
  @Input() public accordionGroupName: string; // = 'status';

  public controlsReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private serviceLinesFilterReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private lossTypesFilterReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public lossStatusGroup: FormGroup = new FormGroup({});

  public products: Array<any> = new Array<any>();//1 dropdown, codeTypes
  public serviceLines: Array<any> = new Array<any>();//2 multi select, serviceLines
  public lossTypes: Array<any> = new Array<any>();//3 multi select, codes
  public lossStatuses: CodeNamePair[];
  public codeClasses;
  public readonly apiFieldSettings: MultiSelectFieldSettings = { value: 'code', text: 'name' };
  public currentProduct: any;
  public codeTypes;

  public delegatedAuthorityOptions: RadioButton[] = [
    { label: 'Delegated', value: 'delegated' },
    { label: 'Non-Delegated', value: 'nonDelegated' },
    { label: 'Both', value: 'both' }
  ];

  constructor(
    private searchFilterService: SearchFilterService,
    private accordionService: AccordionService
  ) {
  }

  clearLossCodes() {
    this.group.get('codes').reset();
  }

  clearServiceLines() {
    this.group.get('serviceLine').reset();
    this.clearLossCodes();
  }

  ngOnInit() {
    this.prepareLossStatusFilters();

    ['codes', 'serviceLine', 'codeClasses']
      .forEach((name) => this.accordionService.addFormControl(this.accordionGroupName, this.group, name));
    this.accordionService.addFormControl(this.accordionGroupName, this.group, 'delegatedAuthority', 'both');
    this.accordionService.setAccordionHasChanges(this.accordionGroupName, false);

    ['codeTypes'].forEach(name => this.group.addControl(name, new FormControl()));

    this.codeTypes = <FormControl>this.group.get('codeTypes');

    const claimSource: string = this.group.value['claimSource'];
    this.loadFilterValues(claimSource);

    this.subscribeLossFilters();
  }

  public loadFilterValues(claimSource: string): void {
    Observable.zip(
      this.searchFilterService.getSearchFilterProducts(claimSource),
      this.searchFilterService.getSearchFilterClasses(claimSource)
    ).subscribe(
      (result) => {
        this.products = result[0];
        this.codeClasses = result[1];
        this.codeTypes.setValue('LossAdjuster');
        this.accordionService.setAccordionControlHasChanges(this.accordionGroupName, 'codeTypes', false);
        this.controlsReady.next(true);
      }
    );
  }

  private filterServiceLines(selectedCodeType: any): void {
    if (selectedCodeType) {
      this.setMultiSelectLabels(selectedCodeType);
      const selectedType = this.products.find(x => x.code === selectedCodeType);
      this.serviceLines = selectedType.serviceLines;
    } else {
      this.serviceLines = [];
    }
    this.serviceLinesFilterReady.next(true);
  }

  private filterLossTypes(selectedServiceLineCodes: any[]): void {
    if (selectedServiceLineCodes) {
      const initial = this.serviceLines
        .filter(x => selectedServiceLineCodes.includes(x.code))
        .map(x => x.lossTypes);

      const collection: any[] = [].concat.apply([], initial);
      this.lossTypes = uniqBy(collection, x => x.code);
    } else {
      this.lossTypes = [];
    }
    this.lossTypesFilterReady.next(true);
  }

  subscribeLossFilters() {
    this.group.get('codeTypes').valueChanges.subscribe(value => this.filterServiceLines(value));
    this.group.get('serviceLine').valueChanges.subscribe(value => this.filterLossTypes(value));
  }

  private prepareLossStatusFilters() {
    this.lossStatuses = this.searchFilterService.lossStatuses;
    this.lossStatuses.forEach(lossStatus => {
      this.accordionService.addFormControl(this.accordionGroupName, this.lossStatusGroup, 'lossStatus' + lossStatus.code);
    });

    this.group.addControl('lossStatusGroup', this.lossStatusGroup);
  }

  private setMultiSelectLabels(selectedCodeType: string) {
    const currentType = this.products.find(x => x.code === selectedCodeType);

    if (currentType) {
      this.currentProduct = currentType;
    }
  }

  getServiceLineLabelName() {
    let retVal = 'Service Line';

    if (this.currentProduct) {
      retVal = this.currentProduct.serviceLineLabelName;
    }

    return retVal;
  }

  getLossTypeCodeLabelName() {
    let retVal = 'Loss Type Code';

    if (this.currentProduct) {
      retVal = this.currentProduct.lossTypeCodeLabelName;
    }

    return retVal;
  }
  /* Restores the codeTypes ServiceLine and Codes MultiSelect serially.
     Directly restoring the from group wont work beacuse the datasources
     of corresponding MultiSlect deoes not have been filter at that time.
     The behaviour subject subscription ensures when to restore the MultiSelect
     once the corresponding dataset has been filled up.
   */
  public restoreStatusSearch(data: any) {

    let linefirst: boolean;
    let lineSec: boolean;

    const codeTypes = this.group.controls.codeTypes;

    codeTypes.setValue(data.codeTypes)
    codeTypes.updateValueAndValidity();

    const serviceLine = this.group.controls.serviceLine;
    const serviceLinesChangeSubscription = this.serviceLinesFilterReady
      .subscribe(() => {
        serviceLine.setValue(data.serviceLine);
        serviceLine.updateValueAndValidity();
      });

    const codes = this.group.controls.codes;
    const lossTypesChangeSubscription = this.lossTypesFilterReady
      .subscribe(() => {
        codes.setValue(data.codes);
        codes.updateValueAndValidity();
      });
    //Unsubscribe the subscription once the 
    //value of dropdown and multi-select restore.
    setTimeout(() => {
      if (lossTypesChangeSubscription)
        lossTypesChangeSubscription.unsubscribe();
      if (serviceLinesChangeSubscription)
        serviceLinesChangeSubscription.unsubscribe();
    }, 1000);

  }
}



// WEBPACK FOOTER //
// D:/a/1/s/UI/src/app/claim/claim-list/claim-status-search/claim-status-search.component.ts